import styled from '@emotion/styled';
import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/Layout/Layout';
import { getQrcode } from '../service/api.v2.service';
import { QrCodeResponse } from '../types/api.v2.types';
import { useQrBlockInfo } from '../hooks/useQrBlockInfo';
// import LanguageSelector from '../components/LanguageSelector'; // UI 제거 (기능은 유지)

interface DownloadButtonProps {
  backgroundColor: string;
}

const QrcodePage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const qrId = new URLSearchParams(location.search).get('qrId');

  const [qrData, setQrData] = useState<QrCodeResponse>({
    imageUrl: '',
    videoUrl: '',
    logoUrl: '',
    color: '',
  });
  const [error, setError] = useState<string | null>(null);
  const [isDownloadingPhoto, setIsDownloadingPhoto] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);
  const [email, setEmail] = useState('');
  const [team, setTeam] = useState('');
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);

  // QR 페이지용 블록 정보 추출 (logoUrl 기반)
  const blockInfo = useQrBlockInfo(qrData.logoUrl);
  // 이메일 수집 완전 비활성화: 모든 환경/블록에서 바로 다운로드 UI 노출
  const shouldShowEmailForm = false;
  
  // localStorage에서 이메일 제출 상태 복원
  const getStoredEmailSubmitted = (): boolean => {
    if (!qrId || !shouldShowEmailForm) {
      return !shouldShowEmailForm;
    }
    const stored = localStorage.getItem(`qrEmailSubmitted_${qrId}`);
    return stored === 'true';
  };

  const [isEmailSubmitted, setIsEmailSubmitted] = useState(getStoredEmailSubmitted());

  // blockInfo가 변경되면 이메일 제출 상태 업데이트
  useEffect(() => {
    if (!shouldShowEmailForm) {
      setIsEmailSubmitted(true);
    } else if (qrId) {
      // shouldShowEmailForm이 true일 때만 localStorage 확인
      const stored = localStorage.getItem(`qrEmailSubmitted_${qrId}`);
      if (stored === 'true') {
        setIsEmailSubmitted(true);
      } else {
        setIsEmailSubmitted(false);
      }
    }
  }, [shouldShowEmailForm, qrId]);

  const getBlockColor = (styleJson: string, index: number = 0): string => {
    try {
      const style = JSON.parse(styleJson) as { [key: number]: string };
      const colors = Object.values(style);
      const color = colors[index];
      return typeof color === 'string' ? color : '';
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchQrcode = () => {
      if (qrId) {
        getQrcode(qrId)
          .then((data) => {
            if (!data) {
              setTimeout(fetchQrcode, 2000);
            } else {
              setQrData(data);
              if (data.videoUrl === '') {
                setTimeout(fetchQrcode, 2000);
              }
            }
          })
          .catch((err) => {
            console.error(err);
            setError(t('qrcode.error.loadFailed'));
          });
      } else {
        setError(t('qrcode.error.idNotFound'));
      }
    };

    fetchQrcode();
  }, [qrId, t]);

  const handlePhotoDownload = async () => {
    flushSync(() => setIsDownloadingPhoto(true));
    try {
      const imageUrl = qrData.imageUrl;
      const response = await fetch(imageUrl, {
        mode: 'cors',
        credentials: 'include',
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('image')) {
        throw new Error(`Unexpected content type: ${contentType}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = 'image.png';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(t('qrcode.error.photoDownload', { error: errorMessage }));
    }
    setIsDownloadingPhoto(false);
  };

  const handleVideoDownload = async () => {
    flushSync(() => setIsDownloadingVideo(true));
    try {
      const videoUrl = qrData.videoUrl;
      const response = await fetch(videoUrl, {
        mode: 'cors',
        credentials: 'include',
      });
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('video')) {
        throw new Error(`Unexpected content type: ${contentType}`);
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = 'video.mp4';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      alert(t('qrcode.error.videoDownload', { error: errorMessage }));
    }
    setIsDownloadingVideo(false);
  };

  const handleEmailSubmit = async () => {
    // 이미 제출 중이면 중복 실행 방지
    if (isSubmittingEmail) {
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      alert(t('qrcode.emailInvalid'));
      return;
    }

    if (!team.trim()) {
      alert(t('qrcode.teamRequired'));
      return;
    }

    setIsSubmittingEmail(true); // 제출 시작

    try {
      // 단일 스트링으로 전송 (이메일/블록정보/팀 순서). blockCode가 없으면 기존처럼 email만 전송.
      const emailWithBlockAndTeam = blockInfo?.blockCode
        ? `${email}/${blockInfo.blockCode}/${team.trim()}`
        : email;
      // 이메일 분석 로깅 통신 중단
      setIsEmailSubmitted(true);
      // localStorage에 이메일 제출 상태 저장 (새로고침 시 복원용)
      if (qrId) {
        localStorage.setItem(`qrEmailSubmitted_${qrId}`, 'true');
      }
    } catch (error) {
      console.error('Email submission failed:', error);
      alert(t('qrcode.error.emailSubmissionFailed'));
    } finally {
      setIsSubmittingEmail(false); // 제출 완료 (성공/실패 관계없이)
    }
  };

  if (error) {
    return <ErrorText>{error}</ErrorText>;
  }

  return (
    <Container style={{ backgroundColor: getBlockColor(qrData.color, 0) }}>
      {/* {isEmailSubmitted && <LanguageSelector />} */} {/* UI 제거 (기능은 유지) */}
      <Logo src={qrData.logoUrl} />
      {isEmailSubmitted && (
        <>
          <CenteredText color={getBlockColor(qrData.color, 1)}>
            {t('qrcode.iphoneInfo')}
          </CenteredText>
          <CenteredText color={getBlockColor(qrData.color, 1)}>
            {qrData.videoUrl === '' && t('qrcode.loading')}
          </CenteredText>
        </>
      )}
      {!isEmailSubmitted ? (
        <EmailFormContainer>
          <MainText color={getBlockColor(qrData.color, 1)}>
            {t('qrcode.emailFormTitle')}
          </MainText>
          <EmailContainer>
            <EmailInput
              type="email"
              placeholder={t('qrcode.emailPlaceholder')}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              backgroundColor={getBlockColor(qrData.color, 3)}
            />
            <EmailInput
              type="text"
              placeholder={t('qrcode.teamPlaceholder')}
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              backgroundColor={getBlockColor(qrData.color, 3)}
            />
            <SubmitButton
              onClick={handleEmailSubmit}
              disabled={isSubmittingEmail}
              backgroundColor={getBlockColor(qrData.color, 2)}
              color={getBlockColor(qrData.color, 4)}
            >
              {isSubmittingEmail ? t('qrcode.submit') + '...' : t('qrcode.submit')}
            </SubmitButton>
          </EmailContainer>
          <PrivacyText color={getBlockColor(qrData.color, 1)}>
            {t('qrcode.emailFormPrivacy')}
          </PrivacyText>
        </EmailFormContainer>
      ) : (
        <ButtonContainer>
          <DownloadButton
            onClick={handlePhotoDownload}
            disabled={qrData.imageUrl === '' || isDownloadingPhoto}
            backgroundColor={getBlockColor(qrData.color, 2)}
            color={getBlockColor(qrData.color, 4)}
          >
            <ButtonText>{t('qrcode.photo')}</ButtonText>
            <ButtonText>{t('qrcode.download')}</ButtonText>
          </DownloadButton>
          <DownloadButton
            onClick={handleVideoDownload}
            disabled={qrData.videoUrl === '' || isDownloadingVideo}
            backgroundColor={getBlockColor(qrData.color, 2)}
            color={getBlockColor(qrData.color, 4)}
          >
            <ButtonText>{t('qrcode.video')}</ButtonText>
            <ButtonText>{t('qrcode.download')}</ButtonText>
          </DownloadButton>
        </ButtonContainer>
      )}
    </Container>
  );
};

const QrcodePageWrapper = () => {
  return (
    <Layout>
      <QrcodePage />
    </Layout>
  );
};

export default QrcodePageWrapper;

const CenteredText = styled.div`
  text-align: center;
  font-size: 24px;
  font-weight: 400;
  line-height: 36px;
  color: ${(props) => props.color};
  white-space: pre-line;
`;

const ErrorText = styled(CenteredText)`
  color: #ef4444;
`;

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1rem;
  gap: 1rem;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  width: 300px;
  max-width: 28rem;
`;

const DownloadButton = styled.button<DownloadButtonProps>`
  background-color: ${(props) => props.backgroundColor};
  color: ${(props) => props.color};
  font-size: 2.25rem;
  font-weight: 600;
  width: 100%;
  height: 10rem;
  border-radius: 0.75rem;
  box-shadow:
    0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  transition: background-color 0.3s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonText = styled.p`
  padding-bottom: 0.5rem;
`;

const Logo = styled.img`
  width: 350px;
`;

const EmailFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
  width: 100%;
  max-width: 36rem;
  padding: 1.5rem;
`;

const MainText = styled.p`
  font-size: 1.125rem;
  font-weight: 600;
  color: ${(props) => props.color || '#1f2937'};
  text-align: center;
  line-height: 1.6;
  margin: 0;
  max-width: 32rem;
`;

const EmailContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  width: 100%;
  max-width: 24rem;
`;

const PrivacyText = styled.p`
  font-size: 0.8rem;
  color: ${(props) => props.color || '#6b7280'};
  text-align: left;
  line-height: 1.4;
  margin: 0;
`;

const EmailInput = styled.input<{ backgroundColor?: string }>`
  width: 100%;
  padding: 1rem;
  font-size: 1.25rem;
  border: 2px solid ${(props) => props.backgroundColor || '#e5e7eb'};
  border-radius: 0.5rem;
  background-color: white;
  outline: none;

  &:focus {
    border-color: ${(props) => props.backgroundColor || '#3b82f6'};
  }
`;

const SubmitButton = styled.button<{ backgroundColor?: string; color?: string }>`
  background-color: ${(props) => props.backgroundColor || '#3b82f6'};
  color: ${(props) => props.color || 'white'};
  font-size: 1.5rem;
  font-weight: 600;
  padding: 1rem 2rem;
  border-radius: 0.5rem;
  transition: opacity 0.3s;

  &:hover {
    opacity: 0.9;
  }
`;
